import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMG_HERO } from "../../lib/assets";
import { useLang } from "../../lib/i18n";

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 1.1, delay: 0.25 + i * 0.16, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Line = ({ children, i, className }) => (
  <span className="block overflow-hidden">
    <motion.span custom={i} variants={lineReveal} initial="hidden" animate="visible" className={`block ${className || ""}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { t, lang } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="accueil" ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#050B14]" data-testid="hero-section">
      <motion.div style={{ y: yImg }} className="absolute inset-0">
        <img src={IMG_HERO} alt="Intérieur parisien d'exception" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050B14]/80 via-[#050B14]/55 to-[#050B14]/90" />
      </motion.div>
      <div className="noise-overlay" />

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 flex flex-col justify-center">
        <div className="max-w-4xl" key={lang}>
          <Line i={0} className="font-script text-[#C9A03C] text-2xl md:text-3xl mb-6">
            {t.hero.eyebrow}
          </Line>
          <h1 className="font-serif-lux text-[#F0F0F0] text-4xl sm:text-5xl lg:text-7xl font-medium leading-[1.08] tracking-tight">
            <Line i={1}>{t.hero.l1}</Line>
            <Line i={2}>
              {t.hero.l2} <em className="text-[#C9A03C] not-italic font-script">{t.hero.l2script}</em>
            </Line>
          </h1>
          <Line i={3} className="mt-8 text-[#F0F0F0]/70 text-base md:text-lg max-w-xl font-light leading-relaxed">
            {t.hero.sub}
          </Line>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 flex flex-wrap items-center gap-6"
          >
            <a
              href="#devis"
              data-testid="hero-cta-devis"
              className="px-9 py-4 bg-[#C9A03C] text-[#050B14] text-[13px] tracking-[0.16em] uppercase font-medium hover:bg-[#F0F0F0] transition-colors duration-400"
            >
              {t.hero.cta1}
            </a>
            <a
              href="#piliers"
              data-testid="hero-cta-discover"
              className="text-[#F0F0F0]/80 text-[13px] tracking-[0.16em] uppercase border-b border-[#C9A03C]/60 pb-1 hover:text-[#C9A03C] transition-colors duration-300"
            >
              {t.hero.cta2}
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <div className="w-px h-14 bg-gradient-to-b from-transparent via-[#C9A03C] to-transparent animate-pulse" />
      </motion.div>
    </section>
  );
};
